import { Campaign, CampaignCreateParams } from '../../../types'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Button from '../../../ui/Button'

export type WizardStep = 'general' | 'audience' | 'advanced'

export const STEPS: WizardStep[] = ['general', 'audience', 'advanced']

interface WizardNavMetadata {
    isLoading: boolean
    isValid: boolean
    disabled: boolean | undefined
}

interface WizardNavProps {
    step: WizardStep
    form: UseFormReturn<CampaignCreateParams>
    campaign?: Campaign
    metadata: WizardNavMetadata
    onBack: () => void
    onNext: () => void
    hasProviders: boolean
}

export default function CampaignWizardNav({ step, form, campaign, metadata, onBack, onNext, hasProviders }: WizardNavProps) {
    const { t } = useTranslation()
    const stepIndex = STEPS.indexOf(step)
    const isFirst = stepIndex === 0

    const campaignType = useWatch({
        control: form.control,
        name: 'type',
    })

    const showAdvancedOption = step === 'audience' && hasProviders

    const name = useWatch({ control: form.control, name: 'name' })
    const channel = useWatch({ control: form.control, name: 'channel' })
    const listIds = useWatch({ control: form.control, name: 'list_ids' })

    const canProceedFromGeneral = !!name && !!channel
    const canProceedFromAudience = campaignType === 'trigger'
        || (listIds && listIds.length > 0)

    let canProceed = true
    if (step === 'general') {
        canProceed = canProceedFromGeneral
    }
    if (step === 'audience') {
        canProceed = !!canProceedFromAudience
    }

    const submitLabel = campaign
        ? t('wizard_btn_save')
        : t('wizard_btn_create')

    return (
        <div className="wizard-nav">
            <div>
                {!isFirst && (
                    <Button
                        variant="secondary"
                        onClick={onBack}
                    >
                        {t('wizard_btn_back')}
                    </Button>
                )}
            </div>
            <div className="wizard-nav-right">
                {showAdvancedOption && (
                    <Button
                        variant="secondary"
                        onClick={onNext}
                        disabled={!canProceed}
                    >
                        {t('wizard_btn_advanced')}
                    </Button>
                )}
                {step === 'general'
                    ? (
                        <Button
                            key="continue"
                            onClick={onNext}
                            disabled={!canProceed}
                        >
                            {t('wizard_btn_continue')}
                        </Button>
                    )
                    : (
                        <Button
                            key="submit"
                            type="submit"
                            isLoading={metadata.isLoading}
                            disabled={!canProceed || metadata.disabled}
                        >
                            {submitLabel}
                        </Button>
                    )}
            </div>
        </div>
    )
}
